import React, { useEffect, useMemo, useState } from "react";
import { Serie } from "@nivo/line";
import { FormControlLabel, Checkbox, Select, MenuItem, FormControl, InputLabel, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";


import { API_BASE_URL } from "../../../config";
import UsageLineChart from "../charts/UsageLineChart";
import ExportDialog, { FilterSummaryEntry } from "../export/ExportDialog";
import AutoCompleteMultiple from "../filters/AutoCompleteMultiple";
import {
  buildLegendConfigs,
  getChartBottomMargin,
  resolveSeriesColors,
} from "../charts/chartUtils";
import {
  sortSeriesByYear,
} from "../../utils/filterUtils";
import {
  UsageResponse,
  AldersgrupperResponse,
  NewUkEmaResponse,
  Disp2Response,
  TreatmentResponse,
} from "../../types";
import "../VetstatDashboard.scss";
import YearRangeSlider from "../../../common/YearRangeSlider";


const DADPDashboardInstance = () => {
  const navigate = useNavigate();
  const [series, setSeries] = useState<Serie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const baseURL = API_BASE_URL || "";

  const [dataset, setDataset] = useState("pigs");
  const [aldersgrupperOptions, setAldersgrupperOptions] = useState<string[]>([]);
  const [selectedAldersgrupper, setSelectedAldersgrupper] = useState<string[]>([]);
  const [aldersgrupperLoading, setAldersgrupperLoading] = useState(true);
  const [aldersgrupperError, setAldersgrupperError] = useState<string | null>(null);

  const [newUkEmaOptions, setNewUkEmaOptions] = useState<string[]>([]);
  const [selectedNewUkEma, setSelectedNewUkEma] = useState<string[]>([]);
  const [newUkEmaLoading, setNewUkEmaLoading] = useState(true);
  const [newUkEmaError, setNewUkEmaError] = useState<string | null>(null);

  const [disp2Options, setDisp2Options] = useState<string[]>([]);
  const [selectedDisp2, setSelectedDisp2] = useState<string[]>([]);
  const [disp2Loading, setDisp2Loading] = useState(true);
  const [disp2Error, setDisp2Error] = useState<string | null>(null);

  const [treatmentOptions, setTreatmentOptions] = useState<string[]>([]);
  const [selectedTreatment, setSelectedTreatment] = useState<string[]>([]);
  const [treatmentLoading, setTreatmentLoading] = useState(true);
  const [treatmentError, setTreatmentError] = useState<string | null>(null);

  const [lockAldersgrupper, setLockAldersgrupper] = useState(true);
  const [lockNewUkEma, setLockNewUkEma] = useState(false);
  const [lockDisp2, setLockDisp2] = useState(true);
  const [lockTreatment, setLockTreatment] = useState(true);

  const [aggregationVariable, setAggregationVariable] = useState("dapd");

  const [minYear, setMinYear] = useState<number>(0);
  const [maxYear, setMaxYear] = useState<number>(0);
  const [selectedYearRange, setSelectedYearRange] = useState<number[]>([0, 0]);

  const aldersgrupperLimit = lockAldersgrupper ? 1 : 10;
  const newUkEmaLimit = lockNewUkEma ? 1 : 10;
  const disp2Limit = lockDisp2 ? 1 : 10;
  const treatmentLimit = lockTreatment ? 1 : 10;

  const visibleLockedFiltersCount = useMemo(() => {
    if (dataset === "intramammary") {
        return (lockNewUkEma ? 1 : 0) + (lockTreatment ? 1 : 0);
    } else {
        return (lockAldersgrupper ? 1 : 0) + (lockNewUkEma ? 1 : 0) + (lockDisp2 ? 1 : 0);
    }
  }, [dataset, lockAldersgrupper, lockNewUkEma, lockDisp2, lockTreatment]);


  const getLockDisabled = (isLocked: boolean) => {
    return isLocked && visibleLockedFiltersCount <= 2;
  };

  const usageParams = useMemo(() => {
    const params = new URLSearchParams();
    params.append("dataset", dataset);
    params.append("aggregation_variable", aggregationVariable);
    selectedAldersgrupper.forEach((value) =>
      params.append("aldersgrupper", value)
    );
    selectedNewUkEma.forEach((value) =>
        params.append("new_uk_ema", value)
    );
    selectedDisp2.forEach((value) =>
        params.append("disp2", value)
    );
    selectedTreatment.forEach((value) =>
        params.append("treatment", value)
    );
    if (selectedYearRange[0] !== 0) {
        params.append("from_year", selectedYearRange[0].toString());
        params.append("to_year", selectedYearRange[1].toString());
    }
    return params;
  }, [selectedAldersgrupper, selectedNewUkEma, selectedDisp2, selectedTreatment, dataset, aggregationVariable, selectedYearRange]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.append("dataset", dataset);
    fetch(baseURL.concat(`/api/vetstat/dadp_year_range?${params.toString()}`))
        .then(res => res.json())
        .then(res => {
            if (res.minYear && res.maxYear) {
                setMinYear(res.minYear);
                setMaxYear(res.maxYear);
                setSelectedYearRange([res.minYear, res.maxYear]);
            }
        })
        .catch(err => console.error("Failed to fetch year range", err));
  }, [baseURL, dataset]);

  useEffect(() => {
    if (dataset === "intramammary") {
        setAldersgrupperOptions([]);
        setAldersgrupperLoading(false);
        return;
    }
    const controller = new AbortController();
    setAldersgrupperLoading(true);
    const params = new URLSearchParams();
    params.append("dataset", dataset);
    selectedNewUkEma.forEach((value) =>
        params.append("new_uk_ema", value)
    );
    selectedDisp2.forEach((value) =>
        params.append("disp2", value)
    );
    const endpoint = baseURL.concat("/api/vetstat/dadp_aldersgrupper");
    const url = params.toString() ? `${endpoint}?${params.toString()}` : endpoint;

    fetch(url, {
      signal: controller.signal,
    })
      .then(async (res) => {
        const payload: AldersgrupperResponse = await res.json();
        if (!res.ok) {
          throw new Error(
            payload?.detail ?? "Could not fetch age groups"
          );
        }
        return payload;
      })
      .then((result) => {
        setAldersgrupperOptions(result.aldersgrupper ?? []);
        setAldersgrupperError(null);
      })
      .catch((err) => {
        if (err instanceof Error && err.name !== "AbortError") {
            setAldersgrupperError(err.message);
        }
      })
      .finally(() => setAldersgrupperLoading(false));

    return () => controller.abort();
  }, [baseURL, selectedNewUkEma, selectedDisp2, dataset]);

  useEffect(() => {
    const controller = new AbortController();
    setNewUkEmaLoading(true);
    const params = new URLSearchParams();
    params.append("dataset", dataset);
    selectedAldersgrupper.forEach((value) =>
        params.append("aldersgrupper", value)
    );
    selectedDisp2.forEach((value) =>
        params.append("disp2", value)
    );
    selectedTreatment.forEach((value) =>
        params.append("treatment", value)
    );
    const endpoint = baseURL.concat("/api/vetstat/dadp_new_uk_ema");
    const url = params.toString() ? `${endpoint}?${params.toString()}` : endpoint;

    fetch(url, {
      signal: controller.signal,
    })
      .then(async (res) => {
        const payload: NewUkEmaResponse = await res.json();
        if (!res.ok) {
          throw new Error(
            payload?.detail ?? "Could not fetch new_uk_ema"
          );
        }
        return payload;
      })
      .then((result) => {
        setNewUkEmaOptions(result.new_uk_ema ?? []);
        setNewUkEmaError(null);
      })
      .catch((err) => {
        if (err instanceof Error && err.name !== "AbortError") {
            setNewUkEmaError(err.message);
        }
      })
      .finally(() => setNewUkEmaLoading(false));

    return () => controller.abort();
    }, [baseURL, selectedAldersgrupper, selectedDisp2, selectedTreatment, dataset]);

    useEffect(() => {
        if (dataset === "intramammary") {
            setDisp2Options([]);
            setDisp2Loading(false);
            return;
        }
        const controller = new AbortController();
        setDisp2Loading(true);
        const params = new URLSearchParams();
        params.append("dataset", dataset);
        selectedAldersgrupper.forEach((value) =>
            params.append("aldersgrupper", value)
        );
        selectedNewUkEma.forEach((value) =>
            params.append("new_uk_ema", value)
        );
        const endpoint = baseURL.concat("/api/vetstat/dadp_disp2");
        const url = params.toString() ? `${endpoint}?${params.toString()}` : endpoint;
    
        fetch(url, {
          signal: controller.signal,
        })
          .then(async (res) => {
            const payload: Disp2Response = await res.json();
            if (!res.ok) {
              throw new Error(
                payload?.detail ?? "Could not fetch disp2"
              );
            }
            return payload;
          })
          .then((result) => {
            setDisp2Options(result.disp2 ?? []);
            setDisp2Error(null);
          })
          .catch((err) => {
            if (err instanceof Error && err.name !== "AbortError") {
                setDisp2Error(err.message);
            }
          })
          .finally(() => setDisp2Loading(false));
    
        return () => controller.abort();
        }, [baseURL, selectedAldersgrupper, selectedNewUkEma, dataset]);

    useEffect(() => {
        if (dataset !== "intramammary") {
            setTreatmentOptions([]);
            setTreatmentLoading(false);
            return;
        }
        const controller = new AbortController();
        setTreatmentLoading(true);
        const params = new URLSearchParams();
        params.append("dataset", dataset);
        selectedNewUkEma.forEach((value) =>
            params.append("new_uk_ema", value)
        );
        const endpoint = baseURL.concat("/api/vetstat/dadp_treatment");
        const url = params.toString() ? `${endpoint}?${params.toString()}` : endpoint;
    
        fetch(url, {
          signal: controller.signal,
        })
          .then(async (res) => {
            const payload: TreatmentResponse = await res.json();
            if (!res.ok) {
              throw new Error(
                payload?.detail ?? "Could not fetch treatment"
              );
            }
            return payload;
          })
          .then((result) => {
            setTreatmentOptions(result.treatment ?? []);
            setTreatmentError(null);
          })
          .catch((err) => {
            if (err instanceof Error && err.name !== "AbortError") {
                setTreatmentError(err.message);
            }
          })
          .finally(() => setTreatmentLoading(false));
    
        return () => controller.abort();
        }, [baseURL, selectedNewUkEma, dataset]);


  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    const usageEndpoint = baseURL.concat("/api/vetstat/dadp_usage_by_year");
    const queryString = usageParams.toString();
    const urlWithParams = queryString
      ? `${usageEndpoint}?${queryString}`
      : usageEndpoint;

    fetch(urlWithParams, { signal: controller.signal })
      .then(async (res) => {
        const payload: UsageResponse = await res.json();
        if (!res.ok) {
          throw new Error(payload?.detail ?? "Could not fetch DADP data");
        }
        return payload;
      })
      .then((result) => {
        const sortedSeries = sortSeriesByYear(result.series ?? []);
        setSeries(sortedSeries);
        setError(null);
      })
      .catch((err) => {
        if (err instanceof Error && err.name !== "AbortError") {
          setError(err.message);
        }
      })
      .finally(() => {
        setLoading(false);
      });

    return () => controller.abort();
  }, [baseURL, usageParams]);

  useEffect(() => {
    if (lockAldersgrupper && selectedAldersgrupper.length > 1) {
        setSelectedAldersgrupper(selectedAldersgrupper.slice(0, 1));
    }
  }, [lockAldersgrupper, selectedAldersgrupper]);

  useEffect(() => {
    if (lockNewUkEma && selectedNewUkEma.length > 1) {
        setSelectedNewUkEma(selectedNewUkEma.slice(0, 1));
    }
  }, [lockNewUkEma, selectedNewUkEma]);

    useEffect(() => {
    if (lockDisp2 && selectedDisp2.length > 1) {
        setSelectedDisp2(selectedDisp2.slice(0, 1));
    }
    }, [lockDisp2, selectedDisp2]);
    
    useEffect(() => {
    if (lockTreatment && selectedTreatment.length > 1) {
        setSelectedTreatment(selectedTreatment.slice(0, 1));
    }
    }, [lockTreatment, selectedTreatment]);

    // Reset filters when dataset changes
    useEffect(() => {
        setSelectedAldersgrupper([]);
        setSelectedNewUkEma([]);
        setSelectedDisp2([]);
        setSelectedTreatment([]);
        // If dataset is intramammary, DADD is not available, so revert to dapd
        if (dataset === "intramammary" && aggregationVariable === "dadd") {
            setAggregationVariable("dapd");
        }
    }, [dataset]);


  const hasData = useMemo(
    () => series.some((entry) => entry.data.length > 0),
    [series]
  );
  
  const resolvedColors = useMemo(
    () => resolveSeriesColors(series),
    [series]
  );

  const legendConfigs = useMemo(
    () => buildLegendConfigs(series, resolvedColors),
    [series, resolvedColors]
  );
  const chartBottomMargin = useMemo(
    () => getChartBottomMargin(legendConfigs.length),
    [legendConfigs.length]
  );

  const handleSelectAldersgrupper = (newValue: string[] | undefined) => {
    setSelectedAldersgrupper((newValue ?? []).slice(0, aldersgrupperLimit));
  };

  const handleSelectNewUkEma = (newValue: string[] | undefined) => {
    setSelectedNewUkEma((newValue ?? []).slice(0, newUkEmaLimit));
    };

    const handleSelectDisp2 = (newValue: string[] | undefined) => {
    setSelectedDisp2((newValue ?? []).slice(0, disp2Limit));
    }

    const handleSelectTreatment = (newValue: string[] | undefined) => {
    setSelectedTreatment((newValue ?? []).slice(0, treatmentLimit));
    }

    const handleSliderChange = (event: Event, newValue: number | number[]) => {
      setSelectedYearRange(newValue as number[]);
    };

    const filtersSummary = useMemo<FilterSummaryEntry[]>(() => {
        const entries: FilterSummaryEntry[] = [
            { label: "Dataset", values: [dataset] },
            { label: "Aggregation", values: [aggregationVariable.toUpperCase()] }
        ];
    
        if (dataset !== "intramammary") {
            if (selectedAldersgrupper.length > 0) {
                 entries.push({ label: "Age Groups", values: selectedAldersgrupper });
            }
             if (selectedDisp2.length > 0) {
                entries.push({ label: "Dosage Form", values: selectedDisp2 });
            }
        }
    
        if (selectedNewUkEma.length > 0) {
            entries.push({ label: "Antibiotics (New_UK_EMA)", values: selectedNewUkEma });
        }
    
        if (dataset === "intramammary" && selectedTreatment.length > 0) {
            entries.push({ label: "Treatment", values: selectedTreatment });
        }
        return entries;
      }, [dataset, selectedAldersgrupper, selectedNewUkEma, selectedDisp2, selectedTreatment, aggregationVariable]);

  return (
    <div className="vetstat-container">
      <header className="appHeader" style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="headerSelectsContainer" style={{ flexDirection: 'row', display: 'flex', gap: '1rem', width: 'auto' }}>
          <FormControl variant="outlined" className="headerSelect" size="small">
            <InputLabel>Dataset</InputLabel>
            <Select
              label="Dataset"
              value={dataset}
              onChange={(e) => setDataset(e.target.value as string)}
              >
              <MenuItem value={"pigs"}>Pigs</MenuItem>
              <MenuItem value={"cattle"}>Cattle</MenuItem>
              <MenuItem value={"intramammary"}>Intramammary</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" className="headerSelect" size="small">
            <InputLabel>Aggregation</InputLabel>
            <Select
              label="Aggregation"
              value={aggregationVariable}
              onChange={(e) => setAggregationVariable(e.target.value as string)}
              disabled={dataset === "intramammary" && aggregationVariable === "dadd"}
              >
              <MenuItem value={"dapd"}>DAPD</MenuItem>
              <MenuItem value={"dadd"} disabled={dataset === "intramammary"}>DADD</MenuItem>
            </Select>
          </FormControl>
        </div>
        <Button variant="contained" color="primary" onClick={() => navigate("/AMU/datasetbuilder")}>
            Go to Dataset Builder
        </Button>
      </header>

      <section className="controls">
        {dataset !== "intramammary" && (
        <div className="controlsGroup">
            <div className="controlsGroupHeader">
                <h2>Age Groups</h2>
            <p className="helperText">
              Select which age group(s) to include in the chart. Use multiple selection to compare groups over time.
            </p>
                <FormControlLabel
                    className="lockToggle"
                    control={
                        <Checkbox
                            color="primary"
                            checked={lockAldersgrupper}
                            onChange={(e) => setLockAldersgrupper(e.target.checked)}
                            disabled={getLockDisabled(lockAldersgrupper)}
                        />
                    }
                    label="Lock to one"
                />
            </div>
            <AutoCompleteMultiple
                id={`dadp-aldersgrupper-select`}
                label="Age Groups"
                options={aldersgrupperOptions}
                disabled={aldersgrupperLoading || !!aldersgrupperError}
                value={selectedAldersgrupper}
                setValue={handleSelectAldersgrupper}
                maxToSelect={aldersgrupperLimit}
            />
            {aldersgrupperLoading && (
                <p className="helperText">Loading age groups...</p>
            )}
            {aldersgrupperError && (
                <p className="helperText helperText--error">{aldersgrupperError}</p>
            )}
        </div>
        )}
        
        <div className="controlsGroup">
            <div className="controlsGroupHeader">
                <h2>Antibiotics (New_UK_EMA)</h2>
            <p className="helperText">
              Select one or more antibiotic categories (New_UK_EMA) to include in the chart.
            </p>
                <FormControlLabel
                    className="lockToggle"
                    control={
                        <Checkbox
                            color="primary"
                            checked={lockNewUkEma}
                            onChange={(e) => setLockNewUkEma(e.target.checked)}
                            disabled={getLockDisabled(lockNewUkEma)}
                        />
                    }
                    label="Lock to one"
                />
            </div>
            <AutoCompleteMultiple
                id={`dadp-newukema-select`}
                label="New_UK_EMA"
                options={newUkEmaOptions}
                disabled={newUkEmaLoading || !!newUkEmaError}
                value={selectedNewUkEma}
                setValue={handleSelectNewUkEma}
                maxToSelect={newUkEmaLimit}
            />
            {newUkEmaLoading && (
                <p className="helperText">Loading New_UK_EMA...</p>
            )}
            {newUkEmaError && (
                <p className="helperText helperText--error">{newUkEmaError}</p>
            )}
        </div>

        {dataset !== "intramammary" && (
        <div className="controlsGroup">
            <div className="controlsGroupHeader">
                <h2>Dosage Form</h2>
            <p className="helperText">
              Select which dosage form(s) to include. Use multiple selection to compare forms.
            </p>
                <FormControlLabel
                    className="lockToggle"
                    control={
                        <Checkbox
                            color="primary"
                            checked={lockDisp2}
                            onChange={(e) => setLockDisp2(e.target.checked)}
                            disabled={getLockDisabled(lockDisp2)}
                        />
                    }
                    label="Lock to one"
                />
            </div>
            <AutoCompleteMultiple
                id={`dadp-disp2-select`}
                label="Dosage Form"
                options={disp2Options}
                disabled={disp2Loading || !!disp2Error}
                value={selectedDisp2}
                setValue={handleSelectDisp2}
                maxToSelect={disp2Limit}
            />
            {disp2Loading && (
                <p className="helperText">Loading Dosage Form...</p>
            )}
            {disp2Error && (
                <p className="helperText helperText--error">{disp2Error}</p>
            )}
        </div>
        )}

        {dataset === "intramammary" && (
        <div className="controlsGroup">
            <div className="controlsGroupHeader">
                <h2>Treatment</h2>
                <FormControlLabel
                    className="lockToggle"
                    control={
                        <Checkbox
                            color="primary"
                            checked={lockTreatment}
                            onChange={(e) => setLockTreatment(e.target.checked)}
                            disabled={getLockDisabled(lockTreatment)}
                        />
                    }
                    label="Lock to one"
                />
            </div>
            <AutoCompleteMultiple
                id={`dadp-treatment-select`}
                label="Treatment"
                options={treatmentOptions}
                disabled={treatmentLoading || !!treatmentError}
                value={selectedTreatment}
                setValue={handleSelectTreatment}
                maxToSelect={treatmentLimit}
            />
            {treatmentLoading && (
                <p className="helperText">Loading Treatment...</p>
            )}
            {treatmentError && (
                <p className="helperText helperText--error">{treatmentError}</p>
            )}
        </div>
        )}
        <div className="controlsGroup">
            <YearRangeSlider
                min={minYear}
                max={maxYear}
                value={selectedYearRange}
                onChange={handleSliderChange}
            />
        </div>
      </section>

      <section className="chartSection">
        <div className="chartActions">
            <ExportDialog
                disabled={loading || !hasData}
                disabledReason={loading ? "The graph is updating - please wait." : "Generate a graph before exporting."}
                series={series}
                filtersSummary={filtersSummary}
                usageQuery={usageParams.toString()}
                baseURL={baseURL}
            />
        </div>
        {error && <div className="status status--error">{error}</div>}
        {loading && <div className="status">Loading...</div>}
        {!error && !loading && !hasData && (
          <div className="status">No data points available.</div>
        )}
        {!error && hasData && (
          <div
            className={'chartContainer'}
            style={{ height: 500 }}
          >
            <UsageLineChart
              data={series}
              colors={resolvedColors}
              legendConfigs={legendConfigs}
              chartBottomMargin={chartBottomMargin}
              yAxisLegend={aggregationVariable.toUpperCase()}
            />
          </div>
        )}
      </section>
    </div>
  );
};

export default DADPDashboardInstance;